
import { formatAuthorForMARC21 } from "./formatter";

// Create a simple MARC21 text export instead
export const exportToMARC21 = async (orderItems, selectedOrderId, filename, publisher = "Storybox.pl", location = "Biblioteka - przy T.Kościuszki 49") => {

    // Arrays to collect both formats
    const marcTextRecords = [];
    const binaryRecords = [];

    orderItems.forEach((item, index) => {
        let record = `=LDR  00000nim a2200325 i 4500\n`;
        const fields = []; // For binary format

        // Leader for binary
        fields.push({ tag: 'LDR', content: '00000nim a2200325 i 4500' });

        // Control fields
        const controlNumber = `${selectedOrderId}${String(index + 1).padStart(3, '0')}`;
        record += `=001  ${controlNumber}\n`;
        fields.push({ tag: '001', content: controlNumber });

        const currentDate = new Date().toISOString().slice(2, 10).replace(/-/g, ''); // YY-MM-DD format
        const pubYear = new Date(item.createdAt).getFullYear();
        const dateField = `${currentDate}s${pubYear}    pl      000 0 pol d||||||`;
        record += `=008  ${dateField}\n`;
        fields.push({ tag: '008', content: dateField });

        // ISBN
        if (item.format?.isbn) {
            record += `=020  \\\\$a${item.format.isbn}\n`;
            fields.push({ tag: '020', indicators: '  ', subfields: [{ code: 'a', content: item.format.isbn }] });
        }

        // Cataloging source
        record += `=040  \\\\\\\\$aLIBPANEL$cLIBPANEL$dLIBPANEL\n`;
        fields.push({
            tag: '040', indicators: '  ', subfields: [
                { code: 'a', content: 'LIBPANEL' },
                { code: 'c', content: 'LIBPANEL' },
                { code: 'd', content: 'LIBPANEL' }
            ]
        });

        // Main author
        const mainAuthor = item.format?.product?.authors?.find(author => author.role === "author");
        if (mainAuthor) {
            const authorFormatted = formatAuthorForMARC21(mainAuthor.name);
            record += `=100  1\\\\$a${authorFormatted}\n`;
            fields.push({ tag: '100', indicators: '1 ', subfields: [{ code: 'a', content: authorFormatted }] });
        }

        // Title
        if (item.format?.product?.title) {
            const authors = item.format.product.authors
                ?.filter(author => author.role === "author")
                ?.map(author => author.name)
                ?.join(", ");

            let titleText = `=245  10\\$a${item.format.product.title}`;
            const titleSubfields = [{ code: 'a', content: item.format.product.title }];

            if (authors) {
                titleText += ` /$c${authors}.`;
                titleSubfields.push({ code: 'c', content: `${authors}.` });
            }
            record += titleText + `\n`;
            fields.push({ tag: '245', indicators: '10', subfields: titleSubfields });
        }

        // Publisher info
        record += `=260  \\\\\\\\$a[Piaseczno] :$b${publisher},$c[${pubYear}].\n`;
        fields.push({
            tag: '260', indicators: '  ', subfields: [
                { code: 'a', content: '[Piaseczno] :' },
                { code: 'b', content: `${publisher},` },
                { code: 'c', content: `[${pubYear}].` }
            ]
        });

        // Physical description for audiobooks
        if (item.formatType === 'audiobook' && item.format?.durationMins) {
            // assume durationMins  is h:mm:00
            const hhmmss = item.format.durationMins.split(':');
            try {
                const hours = parseInt(hhmmss[0], 10);
                const minutes = parseInt(hhmmss[1], 10);

                const physDesc = `1 płyta audio (CD) (${hours} godz. ${minutes} min) :$c12 cm.`;
                record += `=300  \\\\\\\\$a${physDesc}\n`;
                fields.push({ tag: '300', indicators: '  ', subfields: [{ code: 'a', content: physDesc }] });
            } catch (error) {
                console.error("Error parsing durationMins:", error);
            }
        }

        // Content type
        switch (item.formatType) {
            case 'audiobook':
                record += `=336  \\\\\\\\$aSłowo mówione$bspw\n`;
                record += `=337  \\\\\\\\$aAudio$bs\n`;
                record += `=337  \\\\\\\\$aKomputer$bc\n`;
                record += `=338  \\\\\\\\$aPłyta audio$bsd\n`;
                if (item.format?.tokenId)
                    record += `=856  40\\\\$uhttp://lib.audiobook.pl/product?id=${item.format?.tokenId || ''}$zZasób online\n`;

                fields.push({
                    tag: '336', indicators: '  ', subfields: [
                        { code: 'a', content: 'Słowo mówione' },
                        { code: 'b', content: 'spw' }
                    ]
                });
                fields.push({
                    tag: '337', indicators: '  ', subfields: [
                        { code: 'a', content: 'Audio' },
                        { code: 'b', content: 's' }
                    ]
                });
                fields.push({
                    tag: '337', indicators: '  ', subfields: [
                        { code: 'a', content: 'Komputer' },
                        { code: 'b', content: 'c' }
                    ]
                });
                fields.push({
                    tag: '338', indicators: '  ', subfields: [
                        { code: 'a', content: 'Płyta audio' },
                        { code: 'b', content: 'sd' }
                    ]
                });
                if (item.format?.tokenId) {
                    record += `=856  40\\\\$u${item.format.tokenId}$zZasób online\n`;
                    fields.push({
                        tag: '856', indicators: '40', subfields: [
                            { code: 'u', content: "http://lib.audiobook.pl/product?id=" + item.format.tokenId },
                            { code: 'z', content: 'Zasób online' }
                        ]
                    });
                }
                break;
            case 'ebook':
                record += `=336  \\\\\\\\$aTekst$btxt\n`;
                record += `=337  \\\\\\\\$aKomputer$bc\n`;
                record += `=338  \\\\\\\\$aZasób online$bcr\n`;

                fields.push({
                    tag: '336', indicators: '  ', subfields: [
                        { code: 'a', content: 'Tekst' },
                        { code: 'b', content: 'txt' }
                    ]
                });
                fields.push({
                    tag: '337', indicators: '  ', subfields: [
                        { code: 'a', content: 'Komputer' },
                        { code: 'b', content: 'c' }
                    ]
                });
                fields.push({
                    tag: '338', indicators: '  ', subfields: [
                        { code: 'a', content: 'Zasób online' },
                        { code: 'b', content: 'cr' }
                    ]
                });
                break;
            default: // physical book
                record += `=336  \\\\\\\\$aTekst$btxt\n`;
                record += `=337  \\\\\\\\$aBez pośrednictwa$bn\n`;
                record += `=338  \\\\\\\\$aWolumin$bnc\n`;
                if (item.format?.tokenId)
                    record += `=856  40\\\\$uhttp://lib.audiobook.pl/product?id=${item.format?.tokenId || ''}$zZasób online\n`;

                fields.push({
                    tag: '336', indicators: '  ', subfields: [
                        { code: 'a', content: 'Tekst' },
                        { code: 'b', content: 'txt' }
                    ]
                });
                fields.push({
                    tag: '337', indicators: '  ', subfields: [
                        { code: 'a', content: 'Bez pośrednictwa' },
                        { code: 'b', content: 'n' }
                    ]
                });
                fields.push({
                    tag: '338', indicators: '  ', subfields: [
                        { code: 'a', content: 'Wolumin' },
                        { code: 'b', content: 'nc' }
                    ]
                });
                if (item.format?.tokenId) {
                    record += `=856  40\\\\$u${item.format.tokenId}$zZasób online\n`;
                    fields.push({
                        tag: '856', indicators: '40', subfields: [
                            { code: 'u', content: "http://lib.audiobook.pl/product?id=" + item.format.tokenId },
                            { code: 'z', content: 'Zasób online' }
                        ]
                    });
                }
        }

        // Narrator
        const narrator = item.format?.product?.authors?.find(author => author.role === "narrator");
        if (narrator) {
            record += `=511  0\\\\$aCzyta: ${narrator.name}.\n`;
            fields.push({ tag: '511', indicators: '0 ', subfields: [{ code: 'a', content: `Czyta: ${narrator.name}.` }] });
        }

        // Additional person entries
        if (narrator) {
            const narratorFormatted = formatAuthorForMARC21(narrator.name);
            record += `=700  1\\\\$a${narratorFormatted}$eWykonanie\n`;
            fields.push({
                tag: '700', indicators: '1 ', subfields: [
                    { code: 'a', content: narratorFormatted },
                    { code: 'e', content: 'Wykonanie' }
                ]
            });
        }

        // Corporate name (publisher)
        record += `=710  2\\\\$a${publisher}$eWydawca\n`;
        fields.push({
            tag: '710', indicators: '2 ', subfields: [
                { code: 'a', content: publisher },
                { code: 'e', content: 'Wydawca' }
            ]
        });

        // Additional ISBN format (like in sample)
        if (item.format?.isbn) {
            record += `=920  \\\\\\\\$a${item.format.isbn}\n`;
            fields.push({ tag: '920', indicators: '  ', subfields: [{ code: 'a', content: item.format.isbn }] });
        }

        // Local system field (923) - matching sample structure
        const price = item.unitPrice ? (item.unitPrice / 100).toFixed(2) : "0.00";
        const orderRef = `${selectedOrderId}/${new Date().getFullYear()}/${String(index + 1).padStart(2, '0')}`;

        const localField = `$zl${location}$ks${item.formatType === 'audiobook' ? 'Audiobook II' : 'Ebook'}$nM-${String(index + 1).padStart(3, '0')}$qKS-M${String(index + 1).padStart(4, '0')}$a${currentDate}$b${orderRef}$xkupno$zze środków budżetowych$c${price}$aLIBPANEL$bpol`;
        record += `=923  \\\\\\\\${localField}\n`;

        // Parse local field for binary format
        const localSubfields = [];
        const localMatches = localField.matchAll(/\$([a-z])([^$]*)/g);
        for (const match of localMatches) {
            localSubfields.push({ code: match[1], content: match[2] });
        }
        fields.push({ tag: '923', indicators: '  ', subfields: localSubfields });

        marcTextRecords.push(record + '\n');
        binaryRecords.push({ fields });
    });

    // Create mnemonic text file
    const marcText = marcTextRecords.join('\n');
    const textBlob = new Blob([marcText], { type: 'text/plain; charset=utf-8' });
    const textUrl = window.URL.createObjectURL(textBlob);
    const textLink = document.createElement('a');
    textLink.href = textUrl;
    textLink.download = `${filename}.mrk`;
    document.body.appendChild(textLink);
    textLink.click();
    document.body.removeChild(textLink);
    window.URL.revokeObjectURL(textUrl);

    // Convert to binary using the mnemonic converter
    const binaryData = mnemonicToBinaryMARC21(marcText);
    const binaryBlob = new Blob([binaryData], { type: 'application/marc' });
    const binaryUrl = window.URL.createObjectURL(binaryBlob);
    const binaryLink = document.createElement('a');
    binaryLink.href = binaryUrl;
    binaryLink.download = `${filename}.mrc`;
    document.body.appendChild(binaryLink);
    binaryLink.click();
    document.body.removeChild(binaryLink);
    window.URL.revokeObjectURL(binaryUrl);
};

function mnemonicToBinaryMARC21(mnemonicText) {
    const records = mnemonicText.split('\n\n').filter(record => record.trim());
    const binaryRecords = records.map(recordText => convertSingleRecord(recordText));

    // Combine all records into one binary stream
    const totalLength = binaryRecords.reduce((sum, record) => sum + record.length, 0);
    const combined = new Uint8Array(totalLength);
    let offset = 0;

    binaryRecords.forEach(record => {
        combined.set(record, offset);
        offset += record.length;
    });

    return combined;
}

function convertSingleRecord(recordText) {
    const lines = recordText.split('\n').filter(line => line.trim());
    const fields = [];
    let leader = '00000nam a2200000 i 4500'; // Default leader

    lines.forEach(line => {
        if (line.startsWith('=LDR')) {
            leader = line.substring(6).trim();
        } else if (line.startsWith('=')) {
            const field = parseMnemonicField(line);
            if (field) fields.push(field);
        }
    });

    return buildBinaryRecord(leader, fields);
}

function parseMnemonicField(line) {
    // Parse lines like: =245  10\$aTitle$cAuthor
    const match = line.match(/^=(\d{3})\s+(.*)$/);
    if (!match) return null;

    const tag = match[1];
    const content = match[2];

    // Control fields (001-009) have no indicators or subfields
    if (tag.startsWith('00')) {
        return {
            tag,
            content: content.replace(/\\\\/g, '') // Remove escaped backslashes
        };
    }

    // Data fields have indicators and subfields
    const indicators = content.substring(0, 2).replace(/\\/g, ' ');
    const subfieldData = content.substring(2);

    const subfields = [];
    const subfieldMatches = subfieldData.matchAll(/\$([a-z0-9])([^$]*)/g);

    for (const match of subfieldMatches) {
        subfields.push({
            code: match[1],
            content: match[2]
        });
    }

    return {
        tag,
        indicators,
        subfields
    };
}

function buildBinaryRecord(leader, fields) {
    const directory = [];
    const fieldData = [];
    let dataOffset = 0;

    // Sort fields by tag
    fields.sort((a, b) => a.tag.localeCompare(b.tag));

    fields.forEach(field => {
        if (!field) return;

        const fieldBytes = buildFieldData(field);

        // Add to directory: tag + length + offset
        directory.push({
            tag: field.tag,
            length: fieldBytes.length,
            offset: dataOffset
        });

        fieldData.push(fieldBytes);
        dataOffset += fieldBytes.length;
    });

    // Build directory string
    let directoryString = '';
    directory.forEach(entry => {
        directoryString += entry.tag +
            entry.length.toString().padStart(4, '0') +
            entry.offset.toString().padStart(5, '0');
    });
    directoryString += '\x1E'; // Field terminator

    // Calculate total record length
    const baseAddress = 24 + directoryString.length;
    const totalLength = baseAddress + dataOffset + 1; // +1 for record terminator

    // Update leader with calculated values
    const updatedLeader =
        totalLength.toString().padStart(5, '0') +
        leader.substring(5, 12) +
        baseAddress.toString().padStart(5, '0') +
        leader.substring(17);

    // Combine all parts
    const encoder = new TextEncoder();
    const leaderBytes = encoder.encode(updatedLeader);
    const directoryBytes = encoder.encode(directoryString);
    const fieldBytes = new Uint8Array(fieldData.reduce((sum, field) => sum + field.length, 0));

    let offset = 0;
    fieldData.forEach(field => {
        fieldBytes.set(field, offset);
        offset += field.length;
    });

    // Final record assembly
    const record = new Uint8Array(totalLength);
    record.set(leaderBytes, 0);
    record.set(directoryBytes, 24);
    record.set(fieldBytes, baseAddress);
    record[totalLength - 1] = 0x1D; // Record terminator

    return record;
}

function buildFieldData(field) {
    const encoder = new TextEncoder();

    if (field.content !== undefined) {
        // Control field
        const data = encoder.encode(field.content + '\x1E');
        return data;
    } else {
        // Data field
        let fieldString = field.indicators;

        field.subfields.forEach(subfield => {
            fieldString += '\x1F' + subfield.code + subfield.content;
        });

        fieldString += '\x1E';
        return encoder.encode(fieldString);
    }
}